import { AI_AUDIT_STATUSES, AISearchResults } from '../models/audit-assistant.model';

type RecordValue = Record<string, unknown>;
const record = (value: unknown): value is RecordValue => !!value && typeof value === 'object' && !Array.isArray(value);
const string = (value: unknown): value is string => typeof value === 'string';
const boolean = (value: unknown) => typeof value === 'boolean';
const integer = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const uuid = (value: unknown) => string(value) && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const date = (value: unknown) => string(value) && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
const timestamp = (value: unknown) => string(value) &&
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value) &&
  date(value.slice(0, 10)) && Number.isFinite(Date.parse(value));
const list = (value: unknown, check: (item: RecordValue) => boolean): boolean =>
  Array.isArray(value) && value.every(item => record(item) && check(item));
const option = (item: RecordValue) => uuid(item['id']) && string(item['name']);

/** Validate the public SSE DTO before allowing nested data into the view. */
export function decodeAISearchResults(value: unknown): AISearchResults {
  if (!record(value) || value['version'] !== 1 || (value['language'] !== 'pl' && value['language'] !== 'en') ||
      (value['search_status'] !== 'catalog_empty' && value['search_status'] !== 'evaluated') ||
      !timestamp(value['as_of']) || !date(value['today'])) throw new Error('Invalid AI results');
  const coverage = value['coverage'];
  if (!record(coverage) || !boolean(coverage['catalog_complete']) ||
      !boolean(coverage['selected_questions_results_complete']) || !integer(coverage['selected_question_count']) ||
      !list(coverage['verified_groups'], option) || !list(coverage['unverified_groups'], option) ||
      !list(coverage['partially_verified_groups'], option)) throw new Error('Invalid AI coverage');
  if (!list(value['audits'], audit => uuid(audit['audit_id']) && string(audit['name']) &&
      AI_AUDIT_STATUSES.some(status => status === audit['status']) && boolean(audit['is_active']) &&
      list(audit['groups'], group => uuid(group['group_id']) && string(group['name']) &&
        uuid(group['checklist_id']) && string(group['checklist_title']) && integer(group['checklist_version']) &&
        list(group['questions'], question => uuid(question['question_id']) && string(question['title']))) &&
      list(audit['locations'], location => {
        const last = location['last_execution'];
        const next = location['next_execution'];
        return uuid(location['location_id']) && string(location['location_name']) &&
          integer(location['role_id']) && string(location['role']) &&
          (last === null || record(last) && uuid(last['execution_plan_id']) &&
            timestamp(last['completed_at']) && boolean(last['completed_at_is_estimated'])) &&
          (next === null || record(next) && uuid(next['execution_plan_id']) && date(next['planned_date']));
      }))) throw new Error('Invalid AI audits');
  return value as unknown as AISearchResults;
}
